// providerDirectoryV2 endpoints now injected into baseApiClient via injectEndpoints
import '../services/api/providerDirectoryV2Service';

import { configureStore, isPlainObject } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import * as Sentry from '@sentry/react';

import { attorneysApiClient } from '@/services/api/attorneysService';
import { baseApiClient } from '@/services/api/baseService';
import { ckeditorApi } from '@/services/api/ckeditorService';
import { clioApiClient } from '@/services/api/clioService';
import { dataRequirementsApi } from '@/services/api/dataRequirementsService';
import { dialpadApi } from '@/services/api/dialpadService';
import { dropboxApiClient } from '@/services/api/dropboxService';
import { googleDriveApiClient } from '@/services/api/googleDriveService';
import { mobrainApiClient } from '@/services/api/mobrainService';
import { quantModeApiClient } from '@/services/api/quantModeService';
import { ringcentralApi } from '@/services/api/ringcentralService';
import { searchApi } from '@/services/api/searchService';
import { slashCommandsApiClient } from '@/services/api/slashCommandsService';
import { voiceAiDashboardApiClient } from '@/services/api/voiceAiDashboardService';

import { agentChatApiClient } from '../services/api/agentChatService';
import { agentChatWorkspaceApiClient } from '../services/api/agentChatWorkspaceService';
import { agentflowConfigApiClient } from '../services/api/agentflowConfigService';
import { agentflowApiClient } from '../services/api/agentflowService';
import { artifactDigestApiClient } from '../services/api/artifactDigestService';
import { artifactApi } from '../services/api/artifactService';
import { audioConversationsApiClient } from '../services/api/audioConversationService';
import { blandCallApi } from '../services/api/blandCallService';
import { blandScheduleApi } from '../services/api/blandScheduleService';
import { blandApiClient } from '../services/api/blandService';
import { blandTemplateApi } from '../services/api/blandtemplateService';
import { blobsApiClient } from '../services/api/blobService';
import { bulkBillingCodeApiClient } from '../services/api/bulkBillingCodeService';
import { callApiClient } from '../services/api/callService';
import { carriersApiService } from '../services/api/carriersApiService';
import { caseContextApprovalApi } from '../services/api/caseContextApprovalService';
import { caseContextProgressApi } from '../services/api/caseContextProgressService';
import { caseIngestionAssistApiClient } from '../services/api/caseIngestionAssistService';
import { caseIngestionApiClient } from '../services/api/caseIngestionService';
import { caseManagerApiClient } from '../services/api/caseManagerService';
import { claimsPanopticonApiClient } from '../services/api/claimsPanopticonService';
import { clientCommunicationsApiClient } from '../services/api/clientCommunicationsService';
import { clientPortalApiClient } from '../services/api/clientPortalService';
import { collectionsApiClient } from '../services/api/collectionService';
import {
  coverageApiClient,
  mentionedCoveragesApiClient,
} from '../services/api/coverageService';
import { crossMatterChangesApiClient } from '../services/api/crossMatterChangesService';
import { deliverablesApi } from '../services/api/deliverablesService';
import { demandExampleApiClient } from '../services/api/demandExampleService';
import { demandExampleTemplateApi } from '../services/api/demandExampleTemplateService';
import { demandProgressApi } from '../services/api/demandProgressService';
import { demandReviewApiClient } from '../services/api/demandReviewService';
import { demandsApiClient } from '../services/api/demandService';
import { documentComparisonApiClient } from '../services/api/documentComparisonService';
import { entitySuggestionsApiClient } from '../services/api/entitySuggestionsService';
import { eventsApi } from '../services/api/eventsService';
import { externalNoteApi } from '../services/api/externalNoteService';
import { externalOpsMessageApiClient } from '../services/api/externalOpsMessageService';
import { externalTasksApiClient } from '../services/api/externalTaskService';
import { faxService } from '../services/api/faxService';
import { feedbackApiClient } from '../services/api/feedbackApiService';
import { firmServicesActivationApi } from '../services/api/firmServicesActivationService';
import { firmsApiClient } from '../services/api/firmsService';
import { firmTaskTemplatesApi } from '../services/api/firmTaskTemplatesService';
import { formsApiClient } from '../services/api/formsService';
import { fridayCheckinApiClient } from '../services/api/fridayCheckinService';
import { frontMessageApi } from '../services/api/frontMessageService';
import {
  incidentApiClient,
  mentionedIncidentsApiClient,
} from '../services/api/incidentService';
import { intakeChaseApiClient } from '../services/api/intakeChaseService';
import { intakeApiClient } from '../services/api/intakeService';
import { leadIntegrationApiClient } from '../services/api/leadIntegrationService';
import { lettersApiClient } from '../services/api/lettersService';
import { matterAssistantApi } from '../services/api/matterAssistant';
import { matterContextApi } from '../services/api/matterContextService';
import { matterFlattenedContextApi } from '../services/api/matterFlattenedContextService';
import { matterNotesExtractionApi } from '../services/api/matterNotesExtractionService';
import { matterNotesApi } from '../services/api/matterNotesService';
import { mattersListApiClient } from '../services/api/mattersListService';
import { mattersApiClient } from '../services/api/mattersService';
import { matterSummaryApi } from '../services/api/matterSummaryService';
import { matterWikiApi } from '../services/api/matterWikiService';
import { navBadgeApiClient } from '../services/api/navBadgeService';
import { notebooksApi } from '../services/api/notebooksService';
import { onlyofficeDemoApi } from '../services/api/onlyofficeDemoService';
import { onlyofficeApi } from '../services/api/onlyofficeService';
import { operatorTemplateApi } from '../services/api/operatorTemplateService';
import { policeDepartmentsApiClient } from '../services/api/policeDepartmentsService';
import { policeReportsApi } from '../services/api/policeReportsService';
import { proofreadingApiClient } from '../services/api/proofreadingService';
import { rainmakerApiClient } from '../services/api/rainmakerService';
import { screeningConfigApiClient } from '../services/api/screeningConfigService';
import { sharedMatterApiClient } from '../services/api/sharedMatterService';
import { staffNotesApi } from '../services/api/staffNotesService';
import { tasksApiClient } from '../services/api/tasksService';
import { transcriptsApiClient } from '../services/api/transcriptService';
import { treatmentCheckInV2ApiClient } from '../services/api/treatmentCheckInV2Api';
import {
  encountersApiClient,
  medicalProvidersApiClient,
  treatmentBarriersApiClient,
  treatmentCheckInsApiClient,
} from '../services/api/treatmentService';
import { userApiClient } from '../services/api/userService';
import { workflowsApiClient } from '../services/api/workflowService';
import { agentChatSlice } from '../services/slices/agentChat';
import { pinnedDocsSlice } from '../services/slices/pinnedDocsSlice';
import { createDispatchGuardMiddleware } from './dispatchGuardMiddleware';
import intakeReducer from './reducers/intake';
import matterReducer from './reducers/matter';
import rootReducer from './reducers/root';

// Create the Sentry Redux enhancer
const sentryReduxEnhancer = Sentry.createReduxEnhancer({});

// Create a list of all API slices for middleware
const apiSlices = [
  mattersApiClient,
  userApiClient,
  callApiClient,
  carriersApiService,
  formsApiClient,
  collectionsApiClient,
  lettersApiClient,
  matterNotesApi,
  matterNotesExtractionApi,
  matterSummaryApi,
  firmsApiClient,
  sharedMatterApiClient,
  tasksApiClient,
  blandTemplateApi,
  matterContextApi,
  blandCallApi,
  blandApiClient,
  policeReportsApi,
  matterAssistantApi,
  blandScheduleApi,
  operatorTemplateApi,
  externalOpsMessageApiClient,
  externalTasksApiClient,
  demandsApiClient,
  policeDepartmentsApiClient,
  mattersListApiClient,
  proofreadingApiClient,
  blobsApiClient,
  agentChatApiClient,
  agentflowApiClient,
  artifactApi,
  artifactDigestApiClient,
  medicalProvidersApiClient,
  encountersApiClient,
  treatmentCheckInsApiClient,
  treatmentBarriersApiClient,
  audioConversationsApiClient,
  transcriptsApiClient,
  workflowsApiClient,
  ckeditorApi,
  agentChatWorkspaceApiClient,
  demandExampleApiClient,
  coverageApiClient,
  mentionedCoveragesApiClient,
  incidentApiClient,
  mentionedIncidentsApiClient,
  staffNotesApi,
  deliverablesApi,
  eventsApi,
  dataRequirementsApi,
  intakeApiClient,
  intakeChaseApiClient,
  dialpadApi,
  ringcentralApi,
  demandExampleTemplateApi,
  matterFlattenedContextApi,
  demandProgressApi,
  demandReviewApiClient,
  caseContextProgressApi,
  caseContextApprovalApi,
  attorneysApiClient,
  baseApiClient,
  faxService,
  feedbackApiClient,
  searchApi,
  entitySuggestionsApiClient,
  frontMessageApi,
  rainmakerApiClient,
  notebooksApi,
  firmServicesActivationApi,
  firmTaskTemplatesApi,
  navBadgeApiClient,
  screeningConfigApiClient,
  clientCommunicationsApiClient,
  caseManagerApiClient,
  claimsPanopticonApiClient,
  crossMatterChangesApiClient,
  caseIngestionApiClient,
  caseIngestionAssistApiClient,
  clientPortalApiClient,
  documentComparisonApiClient,
  onlyofficeDemoApi,
  onlyofficeApi,
  dropboxApiClient,
  clioApiClient,
  googleDriveApiClient,
  voiceAiDashboardApiClient,
  fridayCheckinApiClient,
  mobrainApiClient,
  matterWikiApi,
  quantModeApiClient,
  slashCommandsApiClient,
  bulkBillingCodeApiClient,
  agentflowConfigApiClient,
  leadIntegrationApiClient,
  externalNoteApi,
  treatmentCheckInV2ApiClient,
];

export const store = configureStore({
  reducer: {
    matters: matterReducer,
    intakes: intakeReducer,
    root: rootReducer,
    ...Object.fromEntries(
      apiSlices.map((api) => [api.reducerPath, api.reducer]),
    ),
    agentChat: agentChatSlice.reducer,
    pinnedDocs: pinnedDocsSlice.reducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        // Treat Blob/File as serializable so endpoints returning binary don't warn
        isSerializable: (value: unknown): boolean => {
          if (value instanceof Blob || value instanceof File) return true;
          if (value === null || value === undefined) return true;
          const valueType = typeof value;
          if (
            valueType === 'string' ||
            valueType === 'number' ||
            valueType === 'boolean'
          ) {
            return true;
          }
          if (Array.isArray(value)) {
            return value.every((item) => {
              const t = typeof item;
              return (
                item === null ||
                t === 'string' ||
                t === 'number' ||
                t === 'boolean' ||
                Array.isArray(item) ||
                isPlainObject(item as object)
              );
            });
          }
          return isPlainObject(value as object);
        },
        // Ignore RTK Query cache trees where binary data can live
        ignoredPaths: apiSlices.flatMap((api) => [
          `${api.reducerPath}.queries`,
          `${api.reducerPath}.mutations`,
        ]),
        // Ignore common RTK Query internal actions that may carry non-serializable payloads
        ignoredActions: apiSlices.flatMap((api) => [
          `${api.reducerPath}/executeQuery/fulfilled`,
          `${api.reducerPath}/executeMutation/fulfilled`,
          `${api.reducerPath}/updateQueryResult`,
          `${api.reducerPath}/queryResultPatched`,
          `${api.reducerPath}/cacheEntryAdded`,
          `${api.reducerPath}/cacheEntryRemoved`,
          `${api.reducerPath}/removeQueryResult`,
        ]),
      },
    })
      // Guard against recursive dispatch stack overflow caused by ~90 RTK Query
      // middleware instances each dispatching during initialisation. Must be
      // placed before all API middleware so it can intercept re-entrant calls.
      .concat(createDispatchGuardMiddleware())
      .concat(...apiSlices.map((api) => api.middleware)),
  devTools: import.meta.env.NODE_ENV !== 'production',
  enhancers: (getDefaultEnhancers) => {
    return getDefaultEnhancers().concat(sentryReduxEnhancer);
  },
});

export type RootState = ReturnType<typeof store.getState>;

setupListeners(store.dispatch);
